import { NgClass, NgStyle } from '@angular/common'
import { Component, OnInit, inject, input, DestroyRef } from '@angular/core'
import { FormsModule } from '@angular/forms'

import debounce from 'lodash/debounce'
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion'
import { TreeNode } from 'primeng/api'
import { InputTextModule } from 'primeng/inputtext'
import { PaginatorModule, PaginatorState } from 'primeng/paginator'
import { Select } from 'primeng/select'
import { SkeletonModule } from 'primeng/skeleton'
import { TreeModule } from 'primeng/tree'

import { CategoryFilterComponent } from 'app/_shared/components/filter-tree/category-filter.component'
import { MobileFiltersComponent } from 'app/_shared/components/mobile-filters/mobile-filters.component'
import { ProductItemComponent } from 'app/_shared/components/product-item/product-item.component'
import { SortDescriptor, SortOption } from 'app/_shared/interfaces/Category'
import { Product } from 'app/_shared/interfaces/Product'
import { ProductListContent, Section } from 'app/_shared/interfaces/config'
import { DUMMY_PRODUCTS } from 'app/_shared/mock/dummy-products'
import { LayoutService } from 'app/_shared/services/layout.service'
import { ProductService } from 'app/_shared/services/product.service'
import { AppConfigService } from 'app/app-config.service'

@Component({
  selector: 'app-product-list',
  standalone: true,
  imports: [
    TreeModule,
    PaginatorModule,
    SkeletonModule,
    ProductItemComponent,
    NgClass,
    CategoryFilterComponent,
    NgStyle,
    InputTextModule,
    Select,
    MobileFiltersComponent,
    FormsModule,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent
  ],
  templateUrl: './product-list.component.html',
  styleUrl: './product-list.component.scss'
})
export class ProductListComponent implements OnInit {
  section = input.required<Section<ProductListContent>>()
  private appConfigService = inject(AppConfigService)
  private productService = inject(ProductService)
  private layout = inject(LayoutService)

  hasCategories = true
  showFilters = false
  isMobile = this.layout.isMobile
  private destroyRef = inject(DestroyRef)

  categories: TreeNode[] = []
  selectedCategories: TreeNode[] = []

  brands: TreeNode[] = []
  selectedBrands: TreeNode[] = []
  brandsLoading = false

  isLoading = false
  products: Product[] = []
  skeletons = Array.from({ length: 8 }, (_, i) => i + 1)
  dummyProducts = DUMMY_PRODUCTS

  productsPerRow = 4
  listAllProducts = false
  productsPerPage = 12
  productsToShow = 12
  leadCategory: number | undefined = undefined

  paginatorOptions = {
    total: 0,
    rows: 12,
    first: 0,
    last_page: 0
  }

  private storefrontId!: number
  selectedCategoryIds: number[] = []

  sortingOptions: SortOption[] = [
    { label: 'Newest', value: { id: 0, name: 'created_at', code: 'desc' } },
    { label: 'Price Low to High', value: { id: 1, name: 'final_price', code: 'asc' } },
    { label: 'Price High to Low', value: { id: 2, name: 'final_price', code: 'desc' } },
    { label: 'Name Asc', value: { id: 3, name: 'name', code: 'asc' } },
    { label: 'Name Desc', value: { id: 4, name: 'name', code: 'desc' } }
  ]
  selectedSort?: SortDescriptor
  search = ''
  mobileFiltersVisible = false
  debounceSearch = debounce(this.getProducts, 500)

  ngOnInit() {
    this.productsPerRow = this.section().content?.productsPerRow ?? 4
    this.listAllProducts = this.section().content?.listAllProducts ?? false
    this.showFilters = this.section().content?.showFilters ?? false
    this.productsPerPage = this.section().content?.productsPerPage ?? 12
    this.productsToShow = this.section().content?.productsToShow ?? 12
    this.storefrontId = this.appConfigService.getAppConfig?.id
    this.selectedCategoryIds = this.section().content?.categories
    this.leadCategory = this.section().content?.leadCategory
    const incoming = this.section().content?.sortingOption as SortDescriptor | undefined

    if (incoming && incoming.name !== 'dont_sort') {
      this.selectedSort = this.sortingOptions.find(o => o.value.id === incoming.id)?.value
    } else {
      this.selectedSort = undefined
    }

    this.paginatorOptions.rows = this.productsPerPage
    this.getProducts(this.paginatorOptions)
  }

  applyFilters() {
    this.paginatorOptions.first = 0
    this.debounceSearch(this.paginatorOptions)
  }

  private appendSortingParams(params: any) {
    if (this.selectedSort) {
      params.sort_field = this.selectedSort.name
      params.sort_order = this.selectedSort.code
    }
    return params
  }

  getProducts({ rows, first }: { rows: number; first: number }) {
    this.isLoading = true

    let params: any = {
      rows,
      page: first + 1,
      'relations[]': ['productThumbnail', 'productVariants', 'productVariants.variantThumbnail']
    }

    params = this.appendSortingParams(params)

    if (this.selectedCategoryIds?.length) {
      params['product_categories[]'] = this.selectedCategoryIds
    }

    if (this.search) {
      params['global_filter'] = this.search
    }
    if (this.leadCategory) {
      params['product_category_id'] = this.leadCategory
    }

    if (this.selectedBrands?.length) params['brands[]'] = this.selectedBrands.map(n => n.data.name)

    this.productService.getWidgetProducts(this.storefrontId, params).subscribe({
      next: res => {
        const total = this.listAllProducts
          ? res.data.total
          : Math.min(res.data.total, this.productsToShow)

        this.products = res.data.data
        this.paginatorOptions = {
          rows: this.productsPerPage,
          total,
          first: res.data.from - 1,
          last_page: Math.ceil(total / this.productsPerPage)
        }
        this.isLoading = false
      },
      error: () => (this.isLoading = false)
    })
  }

  onPageChange(e: PaginatorState) {
    this.paginatorOptions.first = e.page!
    this.getProducts(this.paginatorOptions)
  }
}
